import React from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  loading = false
}) => {
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  const handlePageClick = (page: number, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (page >= 1 && page <= totalPages && !loading) {
      onPageChange(page);
    }
  };

  const getPageHref = (page: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    return url.toString();
  };

  return (
    <div className="flex flex-col items-center gap-4 py-4" style={{ minHeight: '120px' }}>
      <div className="text-sm text-muted-foreground">
        Showing {startItem}-{endItem} of {totalItems} items
      </div>
      
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious 
              href={currentPage > 1 ? getPageHref(currentPage - 1) : undefined}
              onClick={(e) => handlePageClick(currentPage - 1, e)}
              className={`${
                currentPage <= 1 || loading 
                  ? 'pointer-events-none opacity-50' 
                  : 'cursor-pointer hover:bg-accent'
              }`}
            />
          </PaginationItem>

          {generatePageNumbers().map((page, index) => (
            <PaginationItem key={index}>
              {page === '...' ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  href={getPageHref(page as number)}
                  onClick={(e) => handlePageClick(page as number, e)}
                  isActive={currentPage === page}
                  className={`cursor-pointer ${
                    loading ? 'pointer-events-none opacity-50' : 'hover:bg-accent'
                  }`}
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext 
              href={currentPage < totalPages ? getPageHref(currentPage + 1) : undefined}
              onClick={(e) => handlePageClick(currentPage + 1, e)}
              className={`${
                currentPage >= totalPages || loading 
                  ? 'pointer-events-none opacity-50' 
                  : 'cursor-pointer hover:bg-accent'
              }`}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};